/**
 * MachineTranslationBanner — dismissible disclaimer rendered when the active
 * namespace bundle was machine-translated (i.e. `_meta.machineTranslated`
 * is true) and the locale is not English.
 *
 * Transparency reasoning (EU AI Act Article 50 spirit + medical-content
 * caution): users on machine-translated pages see a small banner above the
 * main content noting the translation provenance. Persistent dismissal
 * stored in localStorage so repeat visitors do not have to dismiss again.
 */

import { useEffect, useState } from "react";

import type { TranslateFn } from "#/lib/i18n.js";
import { getNamespaceMeta } from "#/lib/i18n.js";
import { DEFAULT_LOCALE, type LocaleCode } from "#/lib/i18n/locales.js";

const DISMISS_STORAGE_KEY_PREFIX = "hantavirus_mt_banner_dismissed";

export interface MachineTranslationBannerProps {
  /** Active locale code (canonical). */
  locale: LocaleCode;
  /** Namespace to consult for `_meta.machineTranslated`. */
  namespace: string;
  /** Translator from `createT(locale, "landing")`. */
  t: TranslateFn;
}

function dismissKey(locale: LocaleCode, namespace: string): string {
  return `${DISMISS_STORAGE_KEY_PREFIX}:${locale}:${namespace}`;
}

export function MachineTranslationBanner({
  locale,
  namespace,
  t,
}: MachineTranslationBannerProps): JSX.Element | null {
  const meta = getNamespaceMeta(locale, namespace);
  const isMachine = locale !== DEFAULT_LOCALE && meta.machineTranslated === true;
  const [dismissed, setDismissed] = useState<boolean>(false);

  // Check dismissal state on mount (client-only — SSR shows the banner
  // optimistically and hides it on hydrate if dismissed).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(dismissKey(locale, namespace));
      if (stored === "1") setDismissed(true);
    } catch {
      // ignore
    }
  }, [locale, namespace]);

  if (!isMachine || dismissed) return null;

  const onDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(dismissKey(locale, namespace), "1");
    } catch {
      // ignore
    }
  };

  return (
    <div
      role="note"
      data-machine-translation-banner
      className="border-b border-[color:var(--color-border-subtle)] bg-[color:var(--color-bg-surface)] px-4 py-2 text-xs text-[color:var(--color-fg-muted)] sm:px-6 lg:px-8"
    >
      <div className="mx-auto flex max-w-7xl items-start justify-between gap-3">
        <p className="flex-1 leading-5">
          <span aria-hidden="true" className="me-1">
            🌐
          </span>
          {t("chrome.machineTranslatedNotice")}
        </p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("chrome.machineTranslatedNoticeDismiss")}
          className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md px-2 py-1 text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-base)] hover:text-[color:var(--color-fg-default)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-info)]"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
